import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useContent } from "@/lib/content";
import { FadeUp, Eyebrow } from "@/components/Motion";
import { imgSrc } from "@/lib/api";

const MATERIALS = [
  { title: "Hardwoods", body: "Spotted gum, ironbark and merbau: dense, durable and beautiful. The right choice for decks and exposed structures that need to endure the weather." },
  { title: "Softwoods & engineered", body: "Treated pine and engineered products for framing and hidden structure. Straight, stable and cost-effective where it counts." },
  { title: "Durability & maintenance", body: "Timber is a living material. We'll tell you honestly how each choice weathers and what it needs from you over the years." },
];

export default function Services() {
  const content = useContent();
  if (!content) return <div className="min-h-screen bg-ivory" />;

  return (
    <main className="pt-[72px]">
      {/* header */}
      <section className="border-b border-charcoal/10 py-16 sm:py-24" data-testid="services-header">
        <div className="wrap">
          <Eyebrow className="mb-6">Services</Eyebrow>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeUp delay={0.05}>
                <h1 className="font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">
                  One carpenter, <em className="italic text-gold">every</em> stage of the build.
                </h1>
              </FadeUp>
            </div>
            <div className="flex items-end lg:col-span-5">
              <FadeUp delay={0.15}>
                <p className="max-w-md leading-relaxed text-charcoal/65">
                  From the frame inside the walls to the final coat of oil on a deck, Heritage
                  Constructions handles the carpentry on your project start to finish, one
                  accountable pair of hands.
                </p>
              </FadeUp>
            </div>
          </div>
          <FadeUp delay={0.2} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-charcoal/10 pt-6">
            {(content.services || []).map((s) => (
              <a key={s.id} href={`#${s.id}`} className="font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/50 transition-colors hover:text-gold">
                {s.num} · {s.title}
              </a>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* service sections */}
      {(content.services || []).map((s, i) => (
        <section key={s.id} id={s.id} className={`scroll-mt-24 py-16 sm:py-24 ${i % 2 === 1 ? "bg-stone/40" : ""}`} data-testid={`service-section-${s.id}`}>
          <div className="wrap grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16">
            <div className={`${i % 2 === 1 ? "lg:order-2 lg:col-span-6" : "lg:col-span-6"}`}>
              <FadeUp>
                {s.image ? (
                  <div className="photo-frame aspect-[4/5] rounded-lg">
                    <img src={imgSrc(s.image)} alt={s.title} loading={i > 1 ? "lazy" : "eager"} />
                  </div>
                ) : (
                  <div className="flex aspect-[4/5] flex-col items-center justify-center gap-4 rounded-lg bg-charcoal p-8 text-center">
                    <span className="font-serif text-6xl text-gold/70">{s.num}</span>
                    <span className="h-px w-10 bg-gold/50" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivory/70">Heritage Constructions</span>
                  </div>
                )}
              </FadeUp>
            </div>
            <div className={`${i % 2 === 1 ? "lg:order-1 lg:col-span-6" : "lg:col-span-6"}`}>
              <FadeUp>
                <p className="font-mono text-sm tracking-[0.25em] text-gold">{s.num}</p>
                <h2 className="mt-3 font-serif text-3xl leading-tight text-charcoal sm:text-4xl lg:text-[2.75rem]">{s.title}</h2>
                <p className="mt-2 font-serif text-xl italic text-charcoal/50">{s.tagline}</p>
                <p className="mt-6 max-w-lg leading-relaxed text-charcoal/70">{s.description}</p>
                <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(s.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-charcoal/75">
                      <Check size={15} className="mt-0.5 shrink-0 text-gold" strokeWidth={2.5} /> {f}
                    </li>
                  ))}
                </ul>
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      {/* materials */}
      <section className="border-t border-charcoal/10 py-20 sm:py-28" data-testid="services-materials">
        <div className="wrap">
          <Eyebrow className="mb-6">Timber & materials</Eyebrow>
          <FadeUp delay={0.05}>
            <h2 className="max-w-2xl font-serif text-3xl leading-tight text-charcoal sm:text-4xl lg:text-5xl">
              The right material, chosen for your site, not a catalogue.
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-charcoal/10 bg-charcoal/10 md:grid-cols-3">
            {MATERIALS.map((m, i) => (
              <FadeUp key={m.title} delay={i * 0.07} className="bg-ivory">
                <div className="h-full p-8 sm:p-10">
                  <h3 className="font-serif text-2xl text-charcoal">{m.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/60">{m.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* process */}
      <section className="bg-charcoal py-20 text-ivory sm:py-28" data-testid="services-process">
        <div className="wrap">
          <Eyebrow className="mb-6" light>The process</Eyebrow>
          <FadeUp delay={0.05}>
            <h2 className="max-w-xl font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">From first call to final walk-through.</h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {(content.process || []).map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.07}>
                <div className="border-t border-ivory/15 pt-6">
                  <p className="font-mono text-xs tracking-[0.25em] text-gold-light">{step.n}</p>
                  <h3 className="mt-4 font-serif text-xl sm:text-2xl">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/55">{step.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-16 flex flex-col items-center gap-4 sm:flex-row">
              <Link to="/contact" className="btn-gold" data-testid="services-cta-quote">Request a Quote <ArrowRight size={16} /></Link>
              <a href="tel:+61456492849" className="btn-ghost-light">Call Lucas: 0456 492 849</a>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
